import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { MatCheckbox } from "@angular/material/checkbox";
import { EMPTY, Subscription } from "rxjs";
import {
  MessageService,
  FeeType,
  PaymentFrequencyLabel,
  DocumentCategory,
  EnumHelper,
  ProductCode,
  AppPageService,
  SessionStorageKey,
  SessionStorageState,
  SignatureMethod,
} from "common";
import { DocuSignSignStepSummary } from "../../loc-agreement/docusign-sign-step/docusign-sign-step.component";
import { SigningSubject, SigningSubjectKind } from "../../loc-agreement/domain/models/docusign-signing.model";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ApplicationAgreementService } from "../application-agreement.service";
import { AcceptApplicationAgreementResponse } from "../application-agreement.data";
import { AgreementDto, OfferBundleInfo, OfferInfo } from "../../agreement/agreement.data";
import { BankAccountService } from "../../bank-account/bank-account.service";
import { SsoAccountFacade, SsoAccountData } from "common";
import { ApplicationData } from "../../application/application.model";
import { catchError } from "rxjs/operators";

@UntilDestroy()
@Component({
    selector: "ifp-application-agreement-form",
    templateUrl: "./application-agreement-form.component.html",
    styleUrls: ["./application-agreement-form.component.scss"],
    standalone: false
})
export class ApplicationAgreementFormComponent implements OnInit {
  data: EntityAgreementFormComponentData;
  productCodes = ProductCode;
  templateId?: number;
  currentOffer: OfferInfo;
  agreementDto: AgreementDto;
  activationMessage: string;
  agreementHtml: SafeHtml;
  bankIntegrationRequired: boolean;
  isDisbursementBankEligible: boolean = false;

  /** Pinned on the application when its MSA offer is accepted; DocuSign replaces the in-house agreement below. */
  isDocuSignSigning: boolean = false;

  /** What the DocuSign sign step signs: this MSA application. */
  signingSubject: SigningSubject;

  /** The figures the DocuSign sign step's summary card presents. */
  docuSignSummary: DocuSignSignStepSummary;

  @ViewChild("agreementCheckbox")
  agreementCheckbox: MatCheckbox;

  @ViewChild("agreementLeft")
  agreementLeft: ElementRef;

  @ViewChild("agreementContent")
  agreementContent: ElementRef;

  @ViewChild("agreementActions")
  agreementActions: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private entityAgreementService: ApplicationAgreementService,
    private messageService: MessageService,
    private ssoAccountFacade: SsoAccountFacade,
    private sanitizer: DomSanitizer,
    private bankAccountService: BankAccountService,
    private _snackBar: MatSnackBar,
    private appPageService: AppPageService
  ) {
    SessionStorageState.set(SessionStorageKey.ApplicationAgreementViewed, true);
    if ((window as any)?.flutter_inappwebview) {
      this.isInWebView = true;
    }
  }

  isInWebView: boolean = false;

  get FeeType() {
    return FeeType;
  }

  get hasDisclosures(): boolean {
    return (this.agreementDto?.disclosures?.length > 0);
  }

  get categoryName() {
    return EnumHelper.getNameFromValue(
      DocumentCategory,
      this.agreementDto?.category
    );
  }

  download() {
    this.entityAgreementService.download(
      this.data.application.id
    );
  }

  reloadComponent() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate([`application/${this.data.application.id}/setup/sign-agreement`]);
  }

  ngOnInit() {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: EntityAgreementFormComponentData) =>
        this.onDataInit(data)
      );

    this.currentOffer = this.data.offerBundlesInfo?.selectedOffer;

    const pendingApplication = this.data?.currentSsoAccount?.pendingApplications?.find(
      app => app.applicationId === this.data?.application?.id
    );
    this.isDisbursementBankEligible = pendingApplication?.errors?.isDisbursementBankEligible ?? false;
    this.isDocuSignSigning = pendingApplication?.signatureMethod === SignatureMethod.DocuSign;

    if (this.isDocuSignSigning) {
      this.prepareDocuSignSigning();
    } else {
      this.entityAgreementService
        .getAgreement(this.data.application.id)
        .pipe(untilDestroyed(this))
        .subscribe((data) => {
          this.templateId = data.templateId;
          this.agreementDto = data;
          this.agreementHtml = this.sanitizer.bypassSecurityTrustHtml(
            this.agreementDto?.html
          );
        });
    }

    this.checkDisbursementAccount();

    this.bankIntegrationRequired = false;
    if (this.data?.application?.entityId) {
      this.bankAccountService
        .getIsBankLinkReminderRequired(this.data.application.entityId)
        .subscribe(
          (isBankLinkReminderRequired) =>
            (this.bankIntegrationRequired = isBankLinkReminderRequired)
        );
    }
  }

  private onDataInit(data: EntityAgreementFormComponentData): void {
    this.data = data;
  }

  private prepareDocuSignSigning(): void {
    this.signingSubject = { kind: SigningSubjectKind.Application, id: this.data.application.id };

    this.docuSignSummary = {
      entityName: this.data.application.entityName,
      amount: this.currentOffer?.amount,
      amountLabel: this.currentOffer?.productCode === ProductCode.Term ? "Loan amount" : "Credit limit",
      repaymentTerm: this.currentOffer?.repaymentTerm,
      paymentFrequencyLabel: PaymentFrequencyLabel.get(this.currentOffer?.paymentFrequency),
      productName: "MSA",
    };
  }

  print() {
    const innerContents = document.getElementById(
      `account-setup-loc-agreement`
    ).innerHTML;
    const popupWinindow = window.open(
      "",
      "_blank",
      "width=600,height=700,scrollbars=no,menubar=no,toolbar=no,location=no,status=no,titlebar=no"
    );
    popupWinindow.document.open();
    popupWinindow.document.write(
      `<html><head><link rel="stylesheet" type="text/css" href="style.css" /></head><body onload="window.print()">${innerContents}</html>`
    );
    popupWinindow.document.close();
  }

  get offerPaymentFrequencyLabel() {
    return PaymentFrequencyLabel.get(this.currentOffer.paymentFrequency);
  }

  review() {
    if (!this.checkDisbursementAccount()) return;

    this.submit();
  }

  hideDisclosure() {
    document.getElementById(
      "account-setup-agreement-disclosure"
    ).style.display = "none";
  }

  private checkDisbursementAccount(): boolean {
    if (!this.isDisbursementBankEligible) {
      this._snackBar.open(
        "Your disbursement bank account is invalid. Please contact your business advisor.",
        "",
        { duration: 5000 }
      );
      return false;
    }
    return true;
  }

  submit() {
    if (this.submitSub) return;

    this.ssoAccountFacade.clearSsoAccountCache();

    this.submitSub = this.entityAgreementService
      .submit(this.data.application.id, {
        accept: true,
        templateId: this.templateId
      })
      .pipe(catchError((error) => {
        return this.submitError(error);
      }))
      .subscribe(this.submitSuccess.bind(this));
  }

  submitError(error: any) {
    this.submitSubClear();
    this.messageService.error(error);
    return EMPTY;
  }

  submitSub: Subscription;

  submitSubClear() {
    if (this.submitSub) this.submitSub.unsubscribe();
    this.submitSub = null;
  }

  submitSuccess(response: AcceptApplicationAgreementResponse) {
    this.submitSubClear();

    if (!response.isFinal) {
      this.reloadComponent();
      return;
    }

    if (response.applicationId) {
      if(!this.appPageService.backToIfPossible(true))
        this.router.navigate(["dashboard"]);
    } else {
      this.messageService.info(
        `Please make sure you checked 'By clicking this checkbox you agree to the terms and conditions set forth in the agreement above.'`
      );
    }
  }
}

export interface EntityAgreementFormComponentData {
  currentSsoAccount: SsoAccountData;
  application: ApplicationData;
  offerBundlesInfo: OfferBundleInfo;
}
